import { useState, useRef, useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Image as ImageIcon, ExternalLink, Package, Lock, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { StockItemQuantitiesTab } from "@/components/stock-item-quantities-tab";
import { StockItemPricingTab } from "@/components/stock-item-pricing-tab";
import { StockItemAnalysisTab } from "@/components/stock-item-analysis-tab";
import { StockItemSupplierTab } from "@/components/stock-item-supplier-tab";
import { StockItemSettingsTab } from "@/components/stock-item-settings-tab";
import { StockItemIntrastatTab } from "@/components/stock-item-intrastat-tab";
import { StockItemLocationsTab } from "@/components/stock-item-locations-tab";
import { CustomFieldsSection } from "@/components/custom-fields-section";
import { StockItemTransactionHistoryTab } from "@/components/stock-item-transaction-history-tab";
import { TransactionType } from "@/types/stock-transaction";
import { mockWarehouses, mockLocations } from "@/data/mockWarehousesLocations";
import { useNavigation } from "@/components/layout/app-layout";
import { StockItem } from "@/types/stock-item";
import { calculateCubicVolume, formatCubicVolume } from "@/lib/cubic-volume";
import { useWarehousesLocations } from "@/hooks/use-warehouses-locations";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";

interface StockItemDrawerDetailsProps {
  item: StockItem;
  isEditMode: boolean;
  onItemChange?: (item: StockItem) => void;
  onRegisterCustomFieldsSaveHandler?: (fn: () => Promise<boolean>) => void;
  /** Whether to fetch from backend. Set to false in demo mode to avoid backend calls. Defaults to true. */
  isLiveMode?: boolean;
  /** Whether this is being displayed in full view mode (affects which tabs are shown) */
  isFullViewMode?: boolean;
  /** Callback for navigating to transaction details */
  onNavigateToTransaction?: (type: string, reference: string, transactionId: string) => void;
}

export function StockItemDrawerDetails({
  item,
  isEditMode,
  onItemChange,
  onRegisterCustomFieldsSaveHandler,
  isLiveMode = true,
  isFullViewMode = false,
  onNavigateToTransaction,
}: StockItemDrawerDetailsProps) {
  const { navigateTo } = useNavigation();
  const [editedItem, setEditedItem] = useState<StockItem>(item);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isPackagingOpen, setIsPackagingOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Backend data hook for warehouses and locations
  const {
    warehouses: backendWarehouses,
    locations: backendLocations,
    isLoadingWarehouses,
    fetchWarehouses,
    fetchLocations,
  } = useWarehousesLocations();

  // Get level 1 label from warehouse hierarchy config for location field
  const { getLevelDisplayName } = useWarehouseHierarchyConfig();
  const level1Label = getLevelDisplayName(1);

  // Fetch warehouses and locations when in live mode
  useEffect(() => {
    if (isLiveMode) {
      fetchWarehouses();
    }
  }, [isLiveMode, fetchWarehouses]);

  // Fetch locations when warehouse changes in live mode
  useEffect(() => {
    if (isLiveMode && editedItem.warehouseCode) {
      fetchLocations({ warehouseCode: editedItem.warehouseCode });
    }
  }, [isLiveMode, editedItem.warehouseCode, fetchLocations]);

  // Get the current data source based on mode
  const warehouses = useMemo(() => {
    return isLiveMode ? backendWarehouses : mockWarehouses;
  }, [isLiveMode, backendWarehouses]);

  const locations = useMemo(() => {
    return isLiveMode ? backendLocations : mockLocations;
  }, [isLiveMode, backendLocations]);

  // Filter locations by selected warehouse
  const availableLocations = useMemo(() => {
    return locations.filter((loc) => loc.warehouseCode === editedItem.warehouseCode && loc.active);
  }, [locations, editedItem.warehouseCode]);

  // Get current warehouse and location details
  const currentWarehouse = useMemo(() => {
    return warehouses.find((w) => w.code === editedItem.warehouseCode);
  }, [warehouses, editedItem.warehouseCode]);

  const currentLocation = useMemo(() => {
    return locations.find((l) => l.locationCode === editedItem.locationCode);
  }, [locations, editedItem.locationCode]);

  // Update edited item when prop changes
  useEffect(() => {
    setEditedItem(item);
    setHasUnsavedChanges(false);
  }, [item]);

  // Handle field changes
  const handleFieldChange = (field: keyof StockItem, value: any) => {
    const updatedItem = { ...editedItem, [field]: value };

    // Auto-calculate cubic volume when dimensions or dimension unit changes
    if (
      field === "dimensionLength" ||
      field === "dimensionWidth" ||
      field === "dimensionHeight" ||
      field === "dimensionUnit"
    ) {
      const length = field === "dimensionLength" ? value : updatedItem.dimensionLength;
      const width = field === "dimensionWidth" ? value : updatedItem.dimensionWidth;
      const height = field === "dimensionHeight" ? value : updatedItem.dimensionHeight;
      const unit = field === "dimensionUnit" ? value : updatedItem.dimensionUnit || "cm";

      updatedItem.cubicVolume = calculateCubicVolume(length, width, height, unit);
    }

    setEditedItem(updatedItem);
    setHasUnsavedChanges(true);
    onItemChange?.(updatedItem);
  };

  // Handle image upload
  const handleImageUpload = (file: File) => {
    if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleFieldChange("image", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (isEditMode) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (isEditMode) {
      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleImageUpload(file);
      }
    }
  };

  // Handle click on upload zone
  const handleUploadZoneClick = () => {
    if (isEditMode) {
      fileInputRef.current?.click();
    }
  };

  // Handle image removal
  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the upload zone click
    if (isEditMode) {
      handleFieldChange("image", "");
    }
  };

  return (
    <div className="space-y-4">
      {/* Unsaved Changes Indicator */}
      {hasUnsavedChanges && (
        <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
          <Badge
            variant="outline"
            className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700"
          >
            Unsaved Changes
          </Badge>
          <span className="text-sm text-amber-800 dark:text-amber-200">You have unsaved changes</span>
        </div>
      )}

      <Tabs defaultValue="details" className="w-full">
        <TabsList className={`grid w-full ${isFullViewMode ? "grid-cols-10" : "grid-cols-9"}`}>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="locations">{level1Label}</TabsTrigger>
          <TabsTrigger value="quantities">Quantities</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="analysis">Analysis</TabsTrigger>
          <TabsTrigger value="supplier">Supplier</TabsTrigger>
          <TabsTrigger value="intrastat">Intrastat</TabsTrigger>
          <TabsTrigger value="custom">Custom</TabsTrigger>
          {isFullViewMode && <TabsTrigger value="transactions">Transactions</TabsTrigger>}
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6 mt-4">
          {/* Image Upload Area */}
          <div className="flex justify-center">
            <div className="relative">
              <div
                className={`
                  w-[200px] h-[200px] rounded-md flex items-center justify-center
                  ${
                    isEditMode
                      ? `border-2 border-dashed cursor-pointer transition-colors ${
                          isDragging
                            ? "border-primary bg-primary/10"
                            : "border-muted-foreground/30 hover:border-primary hover:bg-muted/50"
                        }`
                      : "bg-muted"
                  }
                `}
                onClick={handleUploadZoneClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {editedItem.image ? (
                  <img
                    src={editedItem.image}
                    alt={editedItem.description}
                    className="w-full h-full object-cover rounded-md"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    {isEditMode ? (
                      <>
                        <Upload className="h-8 w-8" />
                        <p className="text-sm text-center px-4">Click to upload or drag image here</p>
                      </>
                    ) : (
                      <ImageIcon className="h-12 w-12" />
                    )}
                  </div>
                )}
              </div>
              {/* Remove Image Button */}
              {isEditMode && editedItem.image && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute -top-2 -right-2 h-8 w-8 rounded-full p-0 shadow-md"
                  onClick={handleRemoveImage}
                  title="Remove image"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handleFileInputChange}
            />
          </div>

          {/* Basic Information Fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stockCode">Stock Code</Label>
              <Input
                id="stockCode"
                value={editedItem.code}
                onChange={(e) => handleFieldChange("code", e.target.value)}
                readOnly={!isEditMode || editedItem.physicalQuantity > 0}
                className={`${!isEditMode || editedItem.physicalQuantity > 0 ? "bg-muted border-0 cursor-default" : ""}`}
              />
              {isEditMode && editedItem.physicalQuantity > 0 && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Cannot be changed after stock has been used
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={editedItem.description}
                onChange={(e) => handleFieldChange("description", e.target.value)}
                readOnly={!isEditMode}
                className={`${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={editedItem.category}
                onValueChange={(value) => handleFieldChange("category", value)}
                disabled={!isEditMode}
              >
                <SelectTrigger id="category" className={`${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Electronics">Electronics</SelectItem>
                  <SelectItem value="Furniture">Furniture</SelectItem>
                  <SelectItem value="Office Supplies">Office Supplies</SelectItem>
                  <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="unitOfMeasure">
                Unit of Measure
              </Label>
              <Select
                value={editedItem.unitOfMeasure}
                onValueChange={(value: StockItem["unitOfMeasure"]) => handleFieldChange("unitOfMeasure", value)}
                disabled={true}
              >
                <SelectTrigger id="unitOfMeasure" className="bg-muted border-0 cursor-not-allowed">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Each">Each</SelectItem>
                  <SelectItem value="Box/Carton">Box/Carton</SelectItem>
                  <SelectItem value="Kg">Kg</SelectItem>
                  <SelectItem value="g">g</SelectItem>
                  <SelectItem value="Litre">Litre</SelectItem>
                  <SelectItem value="Metre">Metre</SelectItem>
                  <SelectItem value="Pallet">Pallet</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Lock className="h-3 w-3" />
                Cannot be changed after creation to preserve transaction history
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="warehouse">Warehouse</Label>
              {isEditMode && warehouses.length > 0 ? (
                <Select
                  value={editedItem.warehouseCode || undefined}
                  onValueChange={(value) => {
                    // Reset location when warehouse changes - update both fields in a single state update
                    // to avoid React's asynchronous state update issue where the second call would use stale state
                    const newWarehouseLocations = locations.filter(
                      (loc) => loc.warehouseCode === value && loc.active,
                    );
                    const newLocationCode = newWarehouseLocations.length > 0 
                      ? newWarehouseLocations[0].locationCode 
                      : "";
                    
                    // Update both warehouseCode and locationCode together
                    const updatedItem = { 
                      ...editedItem, 
                      warehouseCode: value,
                      locationCode: newLocationCode
                    };
                    setEditedItem(updatedItem);
                    setHasUnsavedChanges(true);
                    onItemChange?.(updatedItem);
                  }}
                >
                  <SelectTrigger id="warehouse">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses
                      .filter((w) => w.active)
                      .map((warehouse) => (
                        <SelectItem key={warehouse.code} value={warehouse.code}>
                          {warehouse.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex items-center justify-between">
                  <Input
                    id="warehouse"
                    value={currentWarehouse?.name || editedItem.warehouseCode || ""}
                    readOnly={!isEditMode}
                    className={isEditMode && isLoadingWarehouses ? "bg-muted/50" : "bg-muted border-0 cursor-default"}
                    placeholder={isLoadingWarehouses ? "Loading warehouses..." : ""}
                  />
                  {!isEditMode && (
                    <Button variant="ghost" size="sm" onClick={() => navigateTo("warehouses-locations")} className="ml-2">
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">{level1Label}</Label>
              {isEditMode ? (
                <Select
                  value={editedItem.locationCode}
                  onValueChange={(value) => handleFieldChange("locationCode", value)}
                  disabled={availableLocations.length === 0}
                >
                  <SelectTrigger id="location">
                    <SelectValue
                      placeholder={availableLocations.length === 0 ? "No locations available" : "Select location"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLocations.map((location) => (
                      <SelectItem key={location.locationCode} value={location.locationCode}>
                        <div className="flex flex-col">
                          <span className="font-mono text-sm">{location.locationCode}</span>
                          <span className="text-xs text-muted-foreground">
                            {location.locationType} • {location.utilizationPercent}% utilized
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="space-y-2">
                  <Input
                    id="location"
                    value={editedItem.locationCode}
                    readOnly
                    className="bg-muted border-0 cursor-default font-mono"
                  />
                  {currentLocation && (
                    <div className="text-xs text-muted-foreground space-y-1 p-2 bg-muted/50 rounded-md">
                      <div className="flex justify-between">
                        <span>Type:</span>
                        <span className="font-medium">{currentLocation.locationType}</span>
                      </div>
                      {currentLocation.aisle && (
                        <div className="flex justify-between">
                          <span>Aisle:</span>
                          <span className="font-mono">{currentLocation.aisle}</span>
                        </div>
                      )}
                      {currentLocation.rack && (
                        <div className="flex justify-between">
                          <span>Rack:</span>
                          <span className="font-mono">{currentLocation.rack}</span>
                        </div>
                      )}
                      {currentLocation.shelf && (
                        <div className="flex justify-between">
                          <span>Shelf:</span>
                          <span className="font-mono">{currentLocation.shelf}</span>
                        </div>
                      )}
                      {currentLocation.bin && (
                        <div className="flex justify-between">
                          <span>Bin:</span>
                          <span className="font-mono">{currentLocation.bin}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Utilisation:</span>
                        <span className="font-medium">{currentLocation.utilizationPercent}%</span>
                      </div>
                      {currentLocation.capacity && (
                        <div className="flex justify-between">
                          <span>Capacity:</span>
                          <span className="font-medium">
                            {currentLocation.currentStock} / {currentLocation.capacity}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Additional Details Fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <div className="flex items-center gap-3">
                <Switch
                  id="status"
                  checked={!editedItem.inactive}
                  onCheckedChange={(checked) => handleFieldChange("inactive", !checked)}
                  disabled={!isEditMode}
                />
                <span className="text-sm text-muted-foreground">{editedItem.inactive ? "Inactive" : "Active"}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="weight">Weight</Label>
              <div className="flex gap-2">
                <Input
                  id="weight"
                  type="number"
                  value={editedItem.weight || ""}
                  onChange={(e) => handleFieldChange("weight", parseFloat(e.target.value) || undefined)}
                  placeholder="0.00"
                  readOnly={!isEditMode}
                  className={`flex-1 ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
                />
                <Select
                  value={editedItem.weightUnit || "kg"}
                  onValueChange={(value: "kg" | "g" | "lbs") => handleFieldChange("weightUnit", value)}
                  disabled={!isEditMode}
                >
                  <SelectTrigger className={`w-[100px] ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="lbs">lbs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="barcode">Barcode</Label>
              <Input
                id="barcode"
                type="text"
                value={editedItem.barcode}
                onChange={(e) => handleFieldChange("barcode", e.target.value)}
                readOnly={!isEditMode}
                className={`${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="binNumber">Bin Number</Label>
              <Input
                id="binNumber"
                type="text"
                value={editedItem.binNumber || ""}
                onChange={(e) => handleFieldChange("binNumber", e.target.value)}
                placeholder="Enter bin number"
                readOnly={!isEditMode}
                className={`${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
              />
            </div>

            {/* Packaging Information Section */}
            <Collapsible open={isPackagingOpen} onOpenChange={setIsPackagingOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between" type="button">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    <span>Packaging Information (Optional)</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{isPackagingOpen ? "Hide" : "Show"}</span>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4 space-y-4 border rounded-lg p-4">
                <div className="space-y-2">
                  <Label htmlFor="packSize">Pack Size (Optional)</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      id="packSize"
                      type="number"
                      value={editedItem.packSize || ""}
                      onChange={(e) =>
                        handleFieldChange("packSize", e.target.value ? parseInt(e.target.value) : undefined)
                      }
                      placeholder="6"
                      readOnly={!isEditMode}
                      className={`flex-1 ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">units per pack</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cartonQuantity">Carton Quantity (Optional)</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      id="cartonQuantity"
                      type="number"
                      value={editedItem.cartonQuantity || ""}
                      onChange={(e) =>
                        handleFieldChange("cartonQuantity", e.target.value ? parseInt(e.target.value) : undefined)
                      }
                      placeholder="24"
                      readOnly={!isEditMode}
                      className={`flex-1 ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">units per carton</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weightPerUnit">Weight per Unit (Optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="weightPerUnit"
                      type="number"
                      value={editedItem.weightPerUnit || ""}
                      onChange={(e) =>
                        handleFieldChange("weightPerUnit", e.target.value ? parseFloat(e.target.value) : undefined)
                      }
                      placeholder="0.5"
                      readOnly={!isEditMode}
                      className={`flex-1 ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
                    />
                    <Select
                      value={editedItem.weightPerUnitUnit || "kg"}
                      onValueChange={(value: "kg" | "g" | "lbs") => handleFieldChange("weightPerUnitUnit", value)}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={`w-[100px] ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="lbs">lbs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dimensions">Dimensions (L × W × H) (Optional)</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      id="dimensionLength"
                      type="number"
                      value={editedItem.dimensionLength || ""}
                      onChange={(e) =>
                        handleFieldChange("dimensionLength", e.target.value ? parseFloat(e.target.value) : undefined)
                      }
                      placeholder="Length"
                      readOnly={!isEditMode}
                      className={`flex-1 ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
                    />
                    <span className="text-muted-foreground">×</span>
                    <Input
                      id="dimensionWidth"
                      type="number"
                      value={editedItem.dimensionWidth || ""}
                      onChange={(e) =>
                        handleFieldChange("dimensionWidth", e.target.value ? parseFloat(e.target.value) : undefined)
                      }
                      placeholder="Width"
                      readOnly={!isEditMode}
                      className={`flex-1 ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
                    />
                    <span className="text-muted-foreground">×</span>
                    <Input
                      id="dimensionHeight"
                      type="number"
                      value={editedItem.dimensionHeight || ""}
                      onChange={(e) =>
                        handleFieldChange("dimensionHeight", e.target.value ? parseFloat(e.target.value) : undefined)
                      }
                      placeholder="Height"
                      readOnly={!isEditMode}
                      className={`flex-1 ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}
                    />
                    <Select
                      value={editedItem.dimensionUnit || "cm"}
                      onValueChange={(value: "cm" | "m" | "in" | "ft") => handleFieldChange("dimensionUnit", value)}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={`w-[100px] ${!isEditMode ? "bg-muted border-0 cursor-default" : ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cm">cm</SelectItem>
                        <SelectItem value="m">m</SelectItem>
                        <SelectItem value="in">in</SelectItem>
                        <SelectItem value="ft">ft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cubicVolume">Cubic Volume (m³)</Label>
                  <Input
                    id="cubicVolume"
                    type="text"
                    value={editedItem.cubicVolume ? formatCubicVolume(editedItem.cubicVolume) : ""}
                    readOnly
                    placeholder="Auto-calculated"
                    className="bg-muted border-0 cursor-default"
                  />
                  <p className="text-xs text-muted-foreground">Automatically calculated from dimensions</p>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-4">
          <StockItemSettingsTab
            item={editedItem}
            isEditMode={isEditMode}
            onItemChange={(updatedItem) => {
              setEditedItem(updatedItem);
              setHasUnsavedChanges(true);
              onItemChange?.(updatedItem);
            }}
          />
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="mt-4">
          <StockItemLocationsTab
            item={editedItem}
            isEditMode={isEditMode}
            onItemChange={(updatedItem) => {
              setEditedItem(updatedItem);
              setHasUnsavedChanges(true);
              onItemChange?.(updatedItem);
            }}
            isLiveMode={isLiveMode}
          />
        </TabsContent>

        {/* Quantities Tab */}
        <TabsContent value="quantities" className="mt-4">
          <StockItemQuantitiesTab
            item={editedItem}
            isEditMode={isEditMode}
            onItemChange={(updatedItem) => {
              setEditedItem(updatedItem);
              setHasUnsavedChanges(true);
              onItemChange?.(updatedItem);
            }}
          />
        </TabsContent>

        {/* Pricing Tab */}
        <TabsContent value="pricing" className="mt-4">
          <StockItemPricingTab
            item={editedItem}
            isEditMode={isEditMode}
            onUpdate={(updates) => {
              Object.entries(updates).forEach(([field, value]) => {
                handleFieldChange(field as keyof StockItem, value);
              });
            }}
          />
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="mt-4">
          <StockItemAnalysisTab
            item={editedItem}
            isEditMode={isEditMode}
            onItemChange={(updatedItem) => {
              setEditedItem(updatedItem);
              setHasUnsavedChanges(true);
              onItemChange?.(updatedItem);
            }}
          />
        </TabsContent>

        {/* Supplier Tab */}
        <TabsContent value="supplier" className="mt-4">
          <StockItemSupplierTab
            item={editedItem}
            isEditMode={isEditMode}
            onItemChange={(updatedItem) => {
              setEditedItem(updatedItem);
              setHasUnsavedChanges(true);
              onItemChange?.(updatedItem);
            }}
          />
        </TabsContent>

        {/* Intrastat Tab */}
        <TabsContent value="intrastat" className="mt-4">
          <StockItemIntrastatTab
            item={editedItem}
            onChange={(field, value) => {
              const updatedItem = { ...editedItem, [field]: value };
              setEditedItem(updatedItem);
              setHasUnsavedChanges(true);
              onItemChange?.(updatedItem);
            }}
            disabled={!isEditMode}
          />
        </TabsContent>

        {/* Custom Fields Tab */}
        <TabsContent value="custom" className="mt-4">
          <CustomFieldsSection
            stockItemId={editedItem.stockItemID || 0}
            isEditMode={isEditMode}
            onValuesChange={() => {
              setHasUnsavedChanges(true);
            }}
            onRegisterSaveHandler={onRegisterCustomFieldsSaveHandler}
            isLiveMode={isLiveMode}
          />
        </TabsContent>

        {/* Transactions Tab - Only shown in full view mode */}
        {isFullViewMode && (
          <TabsContent value="transactions" className="mt-4">
            <StockItemTransactionHistoryTab
              stockItemCode={editedItem.code}
              onNavigateToTransaction={(type, reference, transactionId) => {
                if (onNavigateToTransaction) {
                  onNavigateToTransaction(type, reference, transactionId);
                }
              }}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
